import React from 'react';
import TransactionalSheet from './TransactionalSheet';
import { DotsLoader } from './SecureInputStage';
import {
  AlertTriangleIcon,
  CheckIcon,
  CreditCardLockIcon,
  LinkBrokenIcon,
  MessageIcon,
  XCircleIcon,
} from './transactional-icons';
import {
  HotelArtifactBookingState,
  cancelledSummary,
  formatMoney,
  formatStayDate,
} from './hotelArtifact';

const STAGE_ICON_SIZE = 32;
const STAGE_ICON_STROKE = (2 * 24) / STAGE_ICON_SIZE;

export interface HotelBookingProgressSummary {
  hotelName: string;
  room: string;
  board: string;
  total: number;
  currency: string;
}

interface HotelBookingProgressSheetProps {
  show: boolean;
  /** Null while the Book request itself is still in flight. */
  state: HotelArtifactBookingState | null;
  summary: HotelBookingProgressSummary;
  assistantName: string;
  /** Where the assistant follows up: WhatsApp-linked artifact pages vs. the web chat. */
  notifyVia: 'whatsapp' | 'chat';
  onDone: () => void;
}

type Stage = {
  kind:
    | 'booking'
    | 'confirmed'
    | 'pending'
    | 'changed'
    | 'failed'
    | 'unknown'
    | 'cancelled';
  title: string;
  body: React.ReactNode;
  busy?: boolean;
};

const STAGE_ICONS: Record<
  Stage['kind'],
  React.FC<{ size?: number; strokeWidth?: number }>
> = {
  booking: CreditCardLockIcon,
  confirmed: CheckIcon,
  pending: MessageIcon,
  changed: AlertTriangleIcon,
  failed: XCircleIcon,
  unknown: LinkBrokenIcon,
  cancelled: XCircleIcon,
};

type NotBookedState = Extract<
  HotelArtifactBookingState,
  { state: 'not_booked' }
>;

/** What happened to the money: only ever "released" while no card was taken from. */
function moneyLine(state: NotBookedState, assistant: string): string {
  const refund = state.refund;
  if (!refund) {
    return 'Nothing was booked; the approved amount has been released.';
  }
  const amount = formatMoney(refund.amount, refund.currency);
  return refund.status === 'pending'
    ? `Nothing was booked. The ${amount} taken from your card is being refunded to it — allow a few working days for it to show.`
    : `Nothing was booked. The ${amount} taken from your card hasn't been returned yet; ${assistant}'s team is arranging the refund and will confirm once it's done.`;
}

function describe(
  state: HotelArtifactBookingState | null,
  summary: HotelBookingProgressSummary,
  assistant: string,
  notifyVia: 'whatsapp' | 'chat',
): Stage {
  if (!state || state.state === 'booking') {
    return {
      kind: 'booking',
      title: 'Booking your room',
      body: (
        <p>
          Checking the exact rate with {summary.hotelName} and confirming{' '}
          {summary.room} · {summary.board} at{' '}
          {formatMoney(summary.total, summary.currency)}. This usually takes a
          few seconds.
        </p>
      ),
      busy: true,
    };
  }
  if (state.state === 'booked') {
    const dates = `${formatStayDate(state.checkIn)} – ${formatStayDate(state.checkOut)}`;
    const guest = `${state.guest.firstName} ${state.guest.lastName}`.trim();
    if (state.status === 'confirmed') {
      return {
        kind: 'confirmed',
        title: 'Booked',
        body: (
          <>
            <p>
              {state.hotelName}, {state.room} · {state.board}, {dates}, for{' '}
              {guest}. Charged {formatMoney(state.total, state.currency)}.
            </p>
            {state.hotelConfirmationCode && (
              <p>
                Hotel confirmation code: <b>{state.hotelConfirmationCode}</b>
              </p>
            )}
            <p className='fine'>{state.cancellationTerms}</p>
            <p className='fine'>
              Any changes or cancellation, just message {assistant}.
            </p>
          </>
        ),
      };
    }
    return {
      kind: 'pending',
      title: 'Placed, awaiting the hotel',
      body: (
        <>
          <p>
            {state.hotelName}, {state.room} · {state.board}, {dates}, for{' '}
            {guest}, at {formatMoney(state.total, state.currency)}. The hotel
            hasn't confirmed it yet — {assistant} will let you know as soon as
            it does.
          </p>
          <p className='fine'>{state.cancellationTerms}</p>
        </>
      ),
    };
  }
  if (state.state === 'awaiting_payment') {
    const channel = notifyVia === 'whatsapp' ? 'via WhatsApp' : 'here in chat';
    return {
      kind: 'pending',
      title: 'Confirming your booking',
      body: (
        <p>
          {assistant} is confirming your booking at {summary.hotelName} and will
          let you know {channel} when it's completed.
        </p>
      ),
    };
  }
  if (state.state === 'cancelled') {
    const dates = `${formatStayDate(state.checkIn)} – ${formatStayDate(state.checkOut)}`;
    return {
      kind: 'cancelled',
      title: 'Cancelled',
      body: (
        <>
          <p>
            {state.hotelName}, {dates}, has been cancelled.{' '}
            {cancelledSummary(state)}
          </p>
          {state.refund.status === 'pending' && (
            <p className='fine'>
              Card refunds can take a few working days to show.
            </p>
          )}
        </>
      ),
    };
  }
  if (state.state === 'unknown') {
    return {
      kind: 'unknown',
      title: "We didn't get an answer from the hotel",
      body: (
        <p>
          The booking may or may not have gone through, so nothing has been
          retried. {assistant} is checking with the hotel and will confirm
          either way
          {state.bookingId ? ` (reference ${state.bookingId})` : ''}.
        </p>
      ),
    };
  }
  switch (state.reason) {
    case 'price_changed':
    case 'terms_changed':
      return {
        kind: 'changed',
        title:
          state.reason === 'price_changed'
            ? 'The price has changed'
            : 'The terms have changed',
        body: (
          <>
            <p>
              {moneyLine(state, assistant)}{' '}
              {state.current
                ? `The hotel now quotes ${formatMoney(state.current.total, state.current.currency)} — ${state.current.cancellationTerms}`
                : 'The hotel returned different terms for this rate.'}
            </p>
            <p className='fine'>
              Tell {assistant} if you'd like to go ahead at the new price, or
              pick another rate.
            </p>
          </>
        ),
      };
    case 'offer_expired':
      return {
        kind: 'changed',
        title: 'This rate is no longer available',
        body: (
          <p>
            {moneyLine(state, assistant)} Ask {assistant} to check what the
            hotel has now.
          </p>
        ),
      };
    case 'already_booked':
      return {
        kind: 'confirmed',
        title: 'Already booked',
        body: <p>This rate was booked earlier — nothing more was charged.</p>,
      };
    case 'missing_guest_details':
      return {
        kind: 'failed',
        title: 'Guest details needed',
        body: (
          <p>
            Nothing was booked. {assistant} will ask you for the guest's name
            and email to complete it.
          </p>
        ),
      };
    case 'insufficient_balance':
      return {
        kind: 'failed',
        title: 'Not enough balance',
        body: (
          <p>
            Nothing was booked or charged. Top up and {assistant} can book this
            rate again.
          </p>
        ),
      };
    case 'payment_failed':
      return {
        kind: 'failed',
        title: 'Payment did not go through',
        body: (
          <p>
            Nothing was booked. {assistant} will tell you what the card issuer
            said and can try another payment method.
          </p>
        ),
      };
    default:
      return {
        kind: 'failed',
        title: "The hotel couldn't take this booking",
        body: (
          <p>
            {moneyLine(state, assistant)} {assistant} can try again or find you
            something else.
          </p>
        ),
      };
  }
}

/**
 * Replaces the confirm-spend sheet after a hotel Book press: a loader while
 * the system checks and books the exact rate, then the typed outcome in
 * place. Held open until the state settles.
 */
const HotelBookingProgressSheet: React.FC<HotelBookingProgressSheetProps> = ({
  show,
  state,
  summary,
  assistantName,
  notifyVia,
  onDone,
}) => {
  const stage = describe(state, summary, assistantName, notifyVia);
  const Icon = STAGE_ICONS[stage.kind];
  const settled = !stage.busy;
  return (
    <TransactionalSheet
      show={show}
      onClose={settled ? onDone : undefined}
      holdOpen={!settled}
      leading={<Icon size={STAGE_ICON_SIZE} strokeWidth={STAGE_ICON_STROKE} />}
      ariaLabelledBy='hotel-booking-title'
      className='hotel-booking-sheet'
      footer={
        settled ? (
          <button className='tx-btn-primary' type='button' onClick={onDone}>
            Done
          </button>
        ) : undefined
      }
    >
      <div
        key={stage.kind}
        className={`stage stage--${stage.kind}`}
        data-testid={`hotel-booking-stage-${stage.kind}`}
      >
        <div className='stage-text'>
          <h2 className='tx-title hero' id='hotel-booking-title'>
            {stage.title}
          </h2>
          {stage.body}
        </div>
        {stage.busy && (
          <div className='stage-wait'>
            <DotsLoader />
          </div>
        )}
      </div>
    </TransactionalSheet>
  );
};

export default HotelBookingProgressSheet;
